import React, { useState, useEffect } from 'react';
import { Sparkles, Megaphone, Copy, Check, Send, Layers, Rocket, Eye, Edit3, Smartphone, Laptop, MessageSquare, Flame, ShieldCheck, TrendingUp } from 'lucide-react';

interface AdCampaignResult {
  googleSearchAds: Array<{ headline: string; description: string }>;
  metaFeedAds: Array<{ hook: string; bodyCopy: string; cta: string }>;
  whatsAppBroadcasts: string[];
  creativePrompt: string;
}

export const AICampaignGenerator: React.FC<{ onCampaignCreated?: () => void }> = ({ onCampaignCreated }) => {
  const [targetState, setTargetState] = useState('Lagos State');
  const [painPoint, setPainPoint] = useState('Band A Tariff Hikes (₦225/kWh) & Grid Outages');
  const [conversionAngle, setConversionAngle] = useState('GRID_EMERGENCY');
  const [loading, setLoading] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState<string | null>(null);
  const [launched, setLaunched] = useState(false);
  const [result, setResult] = useState<AdCampaignResult | null>(null);

  // Editable ad fields for real-time live mockup updates
  const [selectedMetaIdx, setSelectedMetaIdx] = useState(0);
  const [selectedGoogleIdx, setSelectedGoogleIdx] = useState(0);

  const [editMetaHook, setEditMetaHook] = useState('');
  const [editMetaBody, setEditMetaBody] = useState('');
  const [editMetaCta, setEditMetaCta] = useState('');

  const [editGoogleHeadline, setEditGoogleHeadline] = useState('');
  const [editGoogleDesc, setEditGoogleDesc] = useState('');

  const [previewDevice, setPreviewDevice] = useState<'mobile' | 'desktop'>('mobile');
  const [previewPlatform, setPreviewPlatform] = useState<'facebook' | 'google' | 'whatsapp'>('facebook');
  const [broadcastSimulated, setBroadcastSimulated] = useState(false);

  const statesList = [
    'Lagos State',
    'Abia State (Umuahia & Aba)',
    'Rivers State (Port Harcourt)',
    'Abuja (FCT)',
  ];

  const painPointsList = [
    'Band A Tariff Hikes (₦225/kWh) & Grid Outages',
    'Petrol Generator Price at ₦1,150/L & Fuel Queues',
    'Commercial Power Costs & High OpEx',
    'Frequent National Grid System Collapses',
  ];

  const anglesList = [
    { id: 'GRID_EMERGENCY', label: '⚡ Grid Emergency & Tariff Outages', desc: 'Focuses on power outages, repeated grid failures, and high Band A costs.' },
    { id: 'ECONOMIC_ROI', label: '📈 Economic ROI & Petrol Savings', desc: 'Emphasizes the mathematical savings of eliminating ₦1,150/L generator fuel.' },
    { id: 'PREMIUM_LUXURY', label: '👑 Premium Luxury Status', desc: 'Appeals to estate residents desiring 24/7 silent comfort with zero diesel smoke.' },
  ];

  const handleGenerate = async () => {
    setLoading(true);
    setLaunched(false);
    setBroadcastSimulated(false);
    try {
      const res = await fetch('/api/admin/growth/generate-ad', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ targetState, painPoint, conversionAngle }),
      });
      const data = await res.json();
      if (data.success) {
        setResult(data);
        // Initialize editable states with the first variants
        if (data.metaFeedAds && data.metaFeedAds.length > 0) {
          setSelectedMetaIdx(0);
          setEditMetaHook(data.metaFeedAds[0].hook);
          setEditMetaBody(data.metaFeedAds[0].bodyCopy);
          setEditMetaCta(data.metaFeedAds[0].cta);
        }
        if (data.googleSearchAds && data.googleSearchAds.length > 0) {
          setSelectedGoogleIdx(0);
          setEditGoogleHeadline(data.googleSearchAds[0].headline);
          setEditGoogleDesc(data.googleSearchAds[0].description);
        }
      }
    } catch (err) {
      console.error("Ad Copy Generation Error:", err);
    } finally {
      setLoading(false);
    }
  };

  // Update editable states when user selects a different variant
  useEffect(() => {
    if (result) {
      if (result.metaFeedAds && result.metaFeedAds[selectedMetaIdx]) {
        setEditMetaHook(result.metaFeedAds[selectedMetaIdx].hook);
        setEditMetaBody(result.metaFeedAds[selectedMetaIdx].bodyCopy);
        setEditMetaCta(result.metaFeedAds[selectedMetaIdx].cta);
      }
    }
  }, [selectedMetaIdx, result]);

  useEffect(() => {
    if (result) {
      if (result.googleSearchAds && result.googleSearchAds[selectedGoogleIdx]) {
        setEditGoogleHeadline(result.googleSearchAds[selectedGoogleIdx].headline);
        setEditGoogleDesc(result.googleSearchAds[selectedGoogleIdx].description);
      }
    }
  }, [selectedGoogleIdx, result]);

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(id);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const handleLaunchToGrowthOS = async () => {
    if (!result) return;
    try {
      // Use the live modified text for the actual campaign launch payload
      const campaignName = `AI [${conversionAngle.replace('_', ' ')}]: ${targetState} - ${editMetaHook.slice(0, 25)}...`;
      await fetch('/api/admin/growth/campaigns', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          platform: previewPlatform === 'google' ? 'GOOGLE_ADS' : 'META_ADS',
          campaignName,
          targetLocation: targetState,
          dailyBudgetNgn: conversionAngle === 'GRID_EMERGENCY' ? 35000 : conversionAngle === 'ECONOMIC_ROI' ? 45000 : 60000,
          adHeadline: editGoogleHeadline || (result.googleSearchAds[0]?.headline),
          adPrimaryText: editMetaBody || (result.metaFeedAds[0]?.bodyCopy),
        }),
      });
      setLaunched(true);
      if (onCampaignCreated) onCampaignCreated();
    } catch (e) {
      console.error("Launch campaign error:", e);
    }
  };

  const handleSendTestBroadcast = () => {
    setBroadcastSimulated(true);
    setTimeout(() => {
      setBroadcastSimulated(false);
    }, 4000);
  };

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 md:p-8 shadow-2xl relative overflow-hidden font-sans">
      <div className="absolute top-0 right-0 w-96 h-96 bg-amber-500/10 rounded-full filter blur-3xl -z-10 pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-emerald-500/10 rounded-full filter blur-3xl -z-10 pointer-events-none"></div>

      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6 border-b border-slate-800 pb-6">
        <div className="flex items-center gap-3">
          <div className="p-3.5 bg-gradient-to-br from-amber-500/20 to-emerald-500/20 border border-amber-500/30 rounded-2xl text-amber-400">
            <Megaphone className="w-6 h-6 animate-pulse" />
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-black text-slate-100 flex flex-wrap items-center gap-2">
              Growth OS Smart AI Campaign Copilot
              <span className="bg-gradient-to-r from-amber-500 to-emerald-500 text-slate-950 text-[10px] font-black tracking-widest uppercase px-3 py-1 rounded-full shadow-md">
                ROAS Optimizer
              </span>
            </h2>
            <p className="text-xs text-slate-400 mt-1">
              Engineered with advanced conversion acceleration frameworks to craft high-impact ad copy and visual creative prompts.
            </p>
          </div>
        </div>
      </div>

      {/* Target Controls & Conversion Sizing Panel */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-slate-950/40 p-4 rounded-2xl border border-slate-800/60">
          <label className="block text-[11px] font-bold text-amber-400 uppercase tracking-widest mb-2 flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 bg-amber-400 rounded-full"></span> Target Market Corridor
          </label>
          <select
            value={targetState}
            onChange={(e) => setTargetState(e.target.value)}
            className="w-full bg-slate-900 border border-slate-700/80 rounded-xl px-3 py-2.5 text-xs font-semibold text-slate-200 focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition-all"
          >
            {statesList.map((st) => (
              <option key={st} value={st}>{st}</option>
            ))}
          </select>
        </div>

        <div className="bg-slate-950/40 p-4 rounded-2xl border border-slate-800/60">
          <label className="block text-[11px] font-bold text-emerald-400 uppercase tracking-widest mb-2 flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full"></span> Regional Pain Matrix
          </label>
          <select
            value={painPoint}
            onChange={(e) => setPainPoint(e.target.value)}
            className="w-full bg-slate-900 border border-slate-700/80 rounded-xl px-3 py-2.5 text-xs font-semibold text-slate-200 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all"
          >
            {painPointsList.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </div>

        <div className="bg-slate-950/40 p-4 rounded-2xl border border-slate-800/60">
          <label className="block text-[11px] font-bold text-cyan-400 uppercase tracking-widest mb-2 flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 bg-cyan-400 rounded-full"></span> Conversion Accelerator Angle
          </label>
          <select
            value={conversionAngle}
            onChange={(e) => setConversionAngle(e.target.value)}
            className="w-full bg-slate-900 border border-slate-700/80 rounded-xl px-3 py-2.5 text-xs font-semibold text-slate-200 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all"
          >
            {anglesList.map((angle) => (
              <option key={angle.id} value={angle.id}>{angle.label}</option>
            ))}
          </select>
        </div>
      </div>

      <button
        onClick={handleGenerate}
        disabled={loading}
        className="w-full bg-gradient-to-r from-amber-500 via-emerald-500 to-cyan-500 hover:brightness-110 text-slate-950 font-black text-xs md:text-sm py-4 rounded-2xl shadow-xl shadow-amber-500/10 transition-all flex items-center justify-center gap-2 mb-8"
      >
        {loading ? (
          <>
            <Sparkles className="w-5 h-5 animate-spin" />
            Synthesizing Conversion Angles & Ad variants with Gemini...
          </>
        ) : (
          <>
            Generate AI Performance Campaign & Live Mockups <Sparkles className="w-5 h-5" />
          </>
        )}
      </button>

      {/* Campaign Result View - Two Column Layout */}
      {result && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 animate-in fade-in slide-in-from-bottom-4 duration-300">
          
          {/* LEFT COLUMN: Controls & Plain Text Copy Blocks (7 columns) */}
          <div className="lg:col-span-7 space-y-6">
            
            {/* Meta Ad Copy Generation Blocks */}
            <div className="bg-slate-950/70 border border-slate-800 rounded-2xl p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xs font-bold text-purple-400 uppercase tracking-widest flex items-center gap-2">
                  <span className="w-2 h-2 bg-purple-500 rounded-full animate-pulse"></span> Meta Feed Ads & Hooks
                </h3>
                <div className="flex gap-1.5">
                  {result.metaFeedAds.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSelectedMetaIdx(idx)}
                      className={`text-[10px] px-2 py-0.5 rounded-md font-bold transition-all ${selectedMetaIdx === idx ? 'bg-purple-500 text-slate-950' : 'bg-slate-800 hover:bg-slate-700 text-slate-400'}`}
                    >
                      v{idx + 1}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-[10px] font-semibold text-slate-400 mb-1">High-Impact Hook (Editable)</label>
                  <input
                    type="text"
                    value={editMetaHook}
                    onChange={(e) => setEditMetaHook(e.target.value)}
                    className="w-full bg-slate-900 border border-slate-800 rounded-xl px-3 py-2 text-xs font-medium text-slate-200 focus:outline-none focus:border-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-semibold text-slate-400 mb-1">Body Text (Editable)</label>
                  <textarea
                    rows={4}
                    value={editMetaBody}
                    onChange={(e) => setEditMetaBody(e.target.value)}
                    className="w-full bg-slate-900 border border-slate-800 rounded-xl px-3 py-2 text-xs font-medium text-slate-200 focus:outline-none focus:border-purple-500 resize-none font-sans leading-relaxed"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[10px] font-semibold text-slate-400 mb-1">Call To Action (CTA)</label>
                    <input
                      type="text"
                      value={editMetaCta}
                      onChange={(e) => setEditMetaCta(e.target.value)}
                      className="w-full bg-slate-900 border border-slate-800 rounded-xl px-3 py-1.5 text-xs font-medium text-slate-200 focus:outline-none focus:border-purple-500"
                    />
                  </div>
                  <div className="flex items-end">
                    <button
                      onClick={() => copyToClipboard(`${editMetaHook}\n\n${editMetaBody}\n\nCTA: ${editMetaCta}`, 'meta_copy')}
                      className="w-full h-[34px] bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-2 border border-slate-700"
                    >
                      {copiedIndex === 'meta_copy' ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                      Copy Meta Copy
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Google Search Ads Block */}
            <div className="bg-slate-950/70 border border-slate-800 rounded-2xl p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xs font-bold text-blue-400 uppercase tracking-widest flex items-center gap-2">
                  <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></span> Google Search Ads
                </h3>
                <div className="flex gap-1.5">
                  {result.googleSearchAds.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSelectedGoogleIdx(idx)}
                      className={`text-[10px] px-2 py-0.5 rounded-md font-bold transition-all ${selectedGoogleIdx === idx ? 'bg-blue-500 text-slate-950' : 'bg-slate-800 hover:bg-slate-700 text-slate-400'}`}
                    >
                      v{idx + 1}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-[10px] font-semibold text-slate-400 mb-1">Google Headline (Editable)</label>
                  <input
                    type="text"
                    value={editGoogleHeadline}
                    onChange={(e) => setEditGoogleHeadline(e.target.value)}
                    className="w-full bg-slate-900 border border-slate-800 rounded-xl px-3 py-2 text-xs font-semibold text-slate-200 focus:outline-none focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-semibold text-slate-400 mb-1">Description (Editable)</label>
                  <textarea
                    rows={2}
                    value={editGoogleDesc}
                    onChange={(e) => setEditGoogleDesc(e.target.value)}
                    className="w-full bg-slate-900 border border-slate-800 rounded-xl px-3 py-2 text-xs font-medium text-slate-200 focus:outline-none focus:border-blue-500 resize-none font-sans"
                  />
                </div>
                <div className="flex justify-end">
                  <button
                    onClick={() => copyToClipboard(`${editGoogleHeadline}\n${editGoogleDesc}`, 'google_copy')}
                    className="px-6 h-[34px] bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-xl text-xs font-bold transition-all flex items-center gap-2 border border-slate-700"
                  >
                    {copiedIndex === 'google_copy' ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                    Copy Google Ad
                  </button>
                </div>
              </div>
            </div>

            {/* WhatsApp Broadcast Block */}
            <div className="bg-slate-950/70 border border-slate-800 rounded-2xl p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xs font-bold text-emerald-400 uppercase tracking-widest flex items-center gap-2">
                  <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span> WhatsApp Broadcast Copy
                </h3>
                <span className="text-[10px] text-slate-500">Instant Outreach Template</span>
              </div>

              <div className="bg-slate-900 p-4 rounded-xl border border-slate-800 relative group">
                <div className="text-xs text-slate-200 whitespace-pre-line leading-relaxed font-mono select-all">
                  {result.whatsAppBroadcasts[0]}
                </div>
                <div className="absolute top-3 right-3 flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-all">
                  <button
                    onClick={() => copyToClipboard(result.whatsAppBroadcasts[0], 'wa_copy')}
                    className="p-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-lg text-xs flex items-center gap-1 border border-slate-700"
                  >
                    {copiedIndex === 'wa_copy' ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>

              <div className="mt-4 flex items-center justify-between">
                <span className="text-[10px] text-slate-500">Test the broadcast sequence in real time</span>
                <button
                  onClick={handleSendTestBroadcast}
                  disabled={broadcastSimulated}
                  className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/30 font-bold text-[10px] px-3.5 py-1.5 rounded-lg transition-all flex items-center gap-1.5"
                >
                  <MessageSquare className="w-3.5 h-3.5" />
                  {broadcastSimulated ? '📱 Sending Broadcast...' : '📱 Push WhatsApp Broadcast'}
                </button>
              </div>
            </div>

            {/* AI Graphic Designer Prompt */}
            <div className="bg-slate-950/70 border border-slate-800 rounded-2xl p-5">
              <h3 className="text-xs font-bold text-amber-400 uppercase tracking-widest mb-2 flex items-center gap-2">
                🎨 Creative Graphic Concept Prompt
              </h3>
              <p className="text-[11px] text-slate-400 mb-3 leading-relaxed">
                Copy this prompt directly into a generative AI tool (like Midjourney, Gemini, or DALL-E) to produce optimal high-converting visual assets.
              </p>
              <div className="bg-slate-900 p-3.5 rounded-xl border border-slate-800 flex items-start justify-between gap-3">
                <span className="text-xs text-slate-300 italic">"{result.creativePrompt}"</span>
                <button
                  onClick={() => copyToClipboard(result.creativePrompt, 'prompt_copy')}
                  className="p-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-xs shrink-0"
                >
                  {copiedIndex === 'prompt_copy' ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>

          </div>

          {/* RIGHT COLUMN: Mobile Smartphone Live Mockups (5 columns) */}
          <div className="lg:col-span-5 flex flex-col">
            
            {/* Devices & Platform Toggle Header */}
            <div className="bg-slate-950 border border-slate-800/80 rounded-2xl p-3 mb-4 flex items-center justify-between gap-2">
              <div className="flex bg-slate-900 p-1 rounded-xl">
                <button
                  onClick={() => setPreviewPlatform('facebook')}
                  className={`text-[10px] font-extrabold px-3 py-1.5 rounded-lg transition-all ${previewPlatform === 'facebook' ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30' : 'text-slate-400 hover:text-slate-200'}`}
                >
                  Meta Ad
                </button>
                <button
                  onClick={() => setPreviewPlatform('google')}
                  className={`text-[10px] font-extrabold px-3 py-1.5 rounded-lg transition-all ${previewPlatform === 'google' ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30' : 'text-slate-400 hover:text-slate-200'}`}
                >
                  Google Search
                </button>
                <button
                  onClick={() => setPreviewPlatform('whatsapp')}
                  className={`text-[10px] font-extrabold px-3 py-1.5 rounded-lg transition-all ${previewPlatform === 'whatsapp' ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30' : 'text-slate-400 hover:text-slate-200'}`}
                >
                  WhatsApp
                </button>
              </div>

              <div className="flex gap-1">
                <button
                  onClick={() => setPreviewDevice('mobile')}
                  className={`p-1.5 rounded-lg ${previewDevice === 'mobile' ? 'bg-slate-800 text-amber-400' : 'text-slate-500'}`}
                  title="Mobile View"
                >
                  <Smartphone className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setPreviewDevice('desktop')}
                  className={`p-1.5 rounded-lg ${previewDevice === 'desktop' ? 'bg-slate-800 text-amber-400' : 'text-slate-500'}`}
                  title="Desktop View"
                >
                  <Laptop className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Smart Phone Wrapper Body */}
            <div className={`bg-slate-950 border-4 border-slate-800/90 rounded-[40px] p-4 flex flex-col shadow-2xl relative transition-all duration-300 ${previewDevice === 'mobile' ? 'aspect-[9/18] max-w-[340px] mx-auto w-full' : 'w-full aspect-[16/10]'} overflow-hidden min-h-[480px]`}>
              
              {/* Speaker & camera notch for mobile */}
              {previewDevice === 'mobile' && (
                <div className="absolute top-2 left-1/2 -translate-x-1/2 w-28 h-4 bg-slate-900 border border-slate-800 rounded-full flex items-center justify-center gap-1.5 z-20">
                  <span className="w-2 h-2 bg-slate-950 rounded-full"></span>
                  <span className="w-10 h-1 bg-slate-800 rounded-full"></span>
                </div>
              )}

              {/* Live Preview Inside */}
              <div className="bg-slate-900 rounded-[30px] p-3 flex-1 flex flex-col overflow-y-auto relative text-slate-100 font-sans mt-2">
                
                {previewPlatform === 'facebook' && (
                  <div className="space-y-3 pt-2 text-slate-200">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-amber-500 border border-amber-400/40 flex items-center justify-center text-slate-950 font-bold text-xs shrink-0">
                        AT
                      </div>
                      <div className="leading-tight">
                        <div className="text-[11px] font-black text-slate-100">Access Tech Nigeria</div>
                        <div className="text-[9px] text-slate-400">Sponsored • Facebook & Instagram</div>
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <div className="text-xs font-bold text-slate-100 leading-snug">{editMetaHook}</div>
                      <div className="text-[11px] text-slate-300 leading-relaxed whitespace-pre-line">{editMetaBody}</div>
                    </div>

                    {/* Meta Ad Simulated Graphic Card */}
                    <div className="border border-slate-800 rounded-xl overflow-hidden relative bg-slate-950">
                      <div className="aspect-[1.91/1] bg-gradient-to-br from-slate-900 via-amber-900/40 to-emerald-950/60 flex flex-col justify-between p-3 relative">
                        <div className="flex items-center gap-1">
                          <div className="bg-amber-500/20 text-amber-300 border border-amber-500/30 text-[8px] font-black px-1.5 py-0.5 rounded-md flex items-center gap-1 uppercase tracking-wider">
                            <Flame className="w-2.5 h-2.5" /> High-conversion asset
                          </div>
                        </div>

                        <div className="space-y-0.5 z-10">
                          <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Access Tech Premium Solar</div>
                          <div className="text-xs font-black text-slate-100 drop-shadow">No Noise. No Outages. Zero Fuel.</div>
                        </div>

                        {/* Aesthetic abstract grid back-up visual representation */}
                        <div className="absolute inset-0 opacity-10 pointer-events-none bg-[radial-gradient(#ffffff_1px,transparent_1px)] [background-size:16px_16px]"></div>
                        <div className="absolute top-2 right-2 p-1.5 bg-slate-900/60 rounded-xl">
                          <ShieldCheck className="w-5 h-5 text-emerald-400" />
                        </div>
                      </div>

                      <div className="p-3 bg-slate-950 border-t border-slate-800/80 flex items-center justify-between gap-2">
                        <div className="leading-tight">
                          <div className="text-[9px] text-slate-500 font-bold uppercase tracking-wider">ACCESSTECHRENEWABLES.NG</div>
                          <div className="text-[11px] font-bold text-slate-200">24/7 Power Security Audit</div>
                        </div>
                        <button className="bg-purple-600 hover:bg-purple-500 text-slate-100 font-extrabold text-[10px] px-3.5 py-1.5 rounded-lg shadow uppercase shrink-0 transition-all">
                          {editMetaCta}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {previewPlatform === 'google' && (
                  <div className="space-y-3 pt-2">
                    <div className="flex items-center gap-1 text-[10px] text-slate-400">
                      <span>Ad</span>
                      <span>•</span>
                      <span className="text-slate-300">https://www.accesstechnologiesandrenewableenergy.online/solar-audit</span>
                    </div>

                    <div className="space-y-1">
                      <div className="text-sm font-bold text-blue-400 hover:underline leading-tight cursor-pointer">
                        {editGoogleHeadline || "Reliable 24/7 Solar Power Nigeria"}
                      </div>
                      <div className="text-[11px] text-slate-300 leading-normal">
                        {editGoogleDesc || "Replace noisy generators and high electricity bills with clean lithium-ion solar solutions. Free engineer audit."}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-800">
                      <div className="bg-slate-950 p-2 rounded-lg border border-slate-800">
                        <div className="text-[10px] font-bold text-blue-400">Free Site Survey</div>
                        <div className="text-[9px] text-slate-500 mt-0.5">Book complete load test.</div>
                      </div>
                      <div className="bg-slate-950 p-2 rounded-lg border border-slate-800">
                        <div className="text-[10px] font-bold text-blue-400">10-Year Warranty</div>
                        <div className="text-[9px] text-slate-500 mt-0.5">Heavy-duty LiFePO4 cells.</div>
                      </div>
                    </div>
                  </div>
                )}

                {previewPlatform === 'whatsapp' && (
                  <div className="flex flex-col h-full pt-1">
                    <div className="bg-emerald-800/20 text-emerald-400 border border-emerald-800/30 p-2 rounded-xl text-[10px] font-bold flex items-center gap-2 mb-3">
                      <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-ping"></span>
                      Simulating Active Direct Message Outreach
                    </div>

                    <div className="bg-emerald-950/40 border border-emerald-800/40 text-emerald-100 rounded-2xl p-3.5 text-[11px] font-medium leading-relaxed font-mono whitespace-pre-line relative shadow-md">
                      {result.whatsAppBroadcasts[0]}
                      <div className="absolute bottom-1 right-2.5 text-[8px] text-emerald-500/80">
                        delivered 1m ago ✓✓
                      </div>
                    </div>

                    {broadcastSimulated && (
                      <div className="mt-4 bg-slate-950 p-3 rounded-xl border border-slate-800 text-center animate-bounce text-[10px] font-bold text-emerald-400">
                        📲 WhatsApp Test Message Sent to Queue!
                      </div>
                    )}
                  </div>
                )}

              </div>
            </div>

            {/* Sync Campaign CTA */}
            <div className="mt-4 pt-3 border-t border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="text-[11px] text-slate-400">
                Ready to execute this optimized campaign to active channels?
              </div>
              <button
                onClick={handleLaunchToGrowthOS}
                className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black text-xs px-5 py-3 rounded-xl shadow-lg shadow-emerald-500/20 transition-all flex items-center justify-center gap-1.5 shrink-0"
              >
                {launched ? (
                  <>
                    <Check className="w-4 h-4" /> Synced to Growth OS!
                  </>
                ) : (
                  <>
                    <Rocket className="w-4 h-4" /> Sync & Launch Campaign
                  </>
                )}
              </button>
            </div>

          </div>

        </div>
      )}
    </div>
  );
};
